import { useState } from 'react';
import { Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem, Alert, Chip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { assetsApi, contractsApi, ticketsApi, pmApi, type ServiceAsset, type AmcContract, type ServiceTicket, type PmSchedule } from '../../api/amc.api';
import { extractApiError } from '../../api/error';

const CATEGORIES = ['hardware', 'software', 'network', 'electrical', 'communication', 'emergency_breakdown'];
const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function CustomerPortalPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ assetId: '', title: '', description: '', category: 'hardware' });
  const [error, setError] = useState<string | null>(null);

  const assets = useQuery({ queryKey: ['portal', 'assets'], queryFn: () => assetsApi.list({ limit: 100 }), enabled: tab === 0 });
  const tickets = useQuery({ queryKey: ['portal', 'tickets'], queryFn: () => ticketsApi.list({ limit: 100 }), enabled: tab === 1 });
  const contracts = useQuery({ queryKey: ['portal', 'contracts'], queryFn: () => contractsApi.list({ limit: 100 }), enabled: tab === 2 });
  const pm = useQuery({ queryKey: ['portal', 'pm'], queryFn: () => pmApi.list({ limit: 100 }), enabled: tab === 3 });

  const raise = useMutation({
    mutationFn: () => ticketsApi.create({ assetId: form.assetId ? Number(form.assetId) : undefined, title: form.title, description: form.description || undefined, category: form.category, source: 'portal' }),
    onSuccess: () => { setOpen(false); qc.invalidateQueries({ queryKey: ['portal', 'tickets'] }); }, onError: (e) => setError(extractApiError(e)),
  });

  const assetCols: Column<ServiceAsset>[] = [
    { key: 'code', label: 'Asset', width: 150 },
    { key: 'charger', label: 'Charger', render: (r) => `${r.chargerBrand ?? '—'} ${r.chargerModel ?? ''}` },
    { key: 'amcEnd', label: 'AMC End', render: (r) => r.amcEnd ?? '—' },
    { key: 'healthStatus', label: 'Health', render: (r) => <StatusBadge status={r.healthStatus} /> },
  ];
  const ticketCols: Column<ServiceTicket & { resolutionRemainingMins?: number | null; resolutionBreached?: boolean }>[] = [
    { key: 'code', label: 'Ticket', width: 140 },
    { key: 'title', label: 'Title' },
    { key: 'priority', label: 'Priority', render: (r) => <Chip size="small" label={r.priority} variant="outlined" /> },
    { key: 'sla', label: 'SLA', render: (r) => (r.resolutionBreached ? <Chip size="small" color="error" label="Breached" /> : <span>{r.resolutionRemainingMins ?? '—'}m</span>) },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];
  const contractCols: Column<AmcContract>[] = [
    { key: 'code', label: 'Contract', width: 150 },
    { key: 'title', label: 'Title' },
    { key: 'coverageType', label: 'Coverage', render: (r) => r.coverageType.replace(/_/g, ' ') },
    { key: 'endDate', label: 'Valid To' },
    { key: 'contractValue', label: 'Value', align: 'right', render: (r) => inr(r.contractValue) },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];
  const pmCols: Column<PmSchedule>[] = [
    { key: 'assetId', label: 'Asset', render: (r) => `#${r.assetId}` },
    { key: 'frequency', label: 'Frequency' },
    { key: 'nextDueDate', label: 'Next Due' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];
  const tableProps = { page: 1, limit: 100, onPageChange: () => undefined, onLimitChange: () => undefined };

  return (
    <>
      <PageHeader title="Customer Portal" subtitle="Your chargers, service tickets, AMC status, SLA, PM schedule and reports." action={tab === 1 && <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setOpen(true); }}>Raise Ticket</Button>} />
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="My Chargers" /><Tab label="My Tickets" /><Tab label="My AMC" /><Tab label="PM Schedule" />
      </Tabs>

      {tab === 0 && <DataTable {...tableProps} columns={assetCols} rows={assets.data?.data ?? []} loading={assets.isLoading} total={assets.data?.meta.total ?? 0} rowKey={(r) => r.id} emptyTitle="No chargers" />}
      {tab === 1 && <DataTable {...tableProps} columns={ticketCols} rows={tickets.data?.data ?? []} loading={tickets.isLoading} total={tickets.data?.meta.total ?? 0} rowKey={(r) => r.id} emptyTitle="No tickets" />}
      {tab === 2 && <DataTable {...tableProps} columns={contractCols} rows={contracts.data?.data ?? []} loading={contracts.isLoading} total={contracts.data?.meta.total ?? 0} rowKey={(r) => r.id} emptyTitle="No contracts" />}
      {tab === 3 && <DataTable {...tableProps} columns={pmCols} rows={pm.data?.data ?? []} loading={pm.isLoading} total={pm.data?.meta.total ?? 0} rowKey={(r) => r.id} emptyTitle="No PM schedules" />}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Raise a Service Ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Charger Asset ID" type="number" value={form.assetId} onChange={(e) => setForm((f) => ({ ...f, assetId: e.target.value }))} fullWidth />
            <TextField label="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} fullWidth />
            <TextField label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth multiline minRows={2} />
            <TextField select label="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} fullWidth>{CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}</TextField>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.title} onClick={() => raise.mutate()}>Submit</Button></DialogActions>
      </Dialog>
    </>
  );
}
